const CONTACT_EMAIL = "chanathip.project@gmail.com";
const LAST_UPDATED = "2026-09-29";

export default function PrivacyPolicyPage() {
  return (
    <div className="min-h-screen bg-background">
      <div className="max-w-2xl mx-auto px-6 py-16 flex flex-col gap-8 text-foreground font-sans">
        <div className="flex flex-col gap-1">
          <h1 className="text-2xl font-bold">Privacy Policy</h1>
          <p className="text-sm text-muted-foreground">
            Last updated: {LAST_UPDATED}
          </p>
        </div>

        <Section title="Who we are">
          <p>
            BOO Expense Tracker ("BOO", "we", "us") is a personal finance
            tracking application. This policy explains what data we collect,
            why, and how it is used and protected.
          </p>
        </Section>

        <Section title="Data we collect">
          <ul className="list-disc list-inside flex flex-col gap-1">
            <li>
              <strong>Account info:</strong> your name, email address, and
              profile picture from Google Sign-In.
            </li>
            <li>
              <strong>Gmail data (optional, separate consent):</strong> if you
              connect Gmail, we access emails matching bank statement patterns
              you configure, using the read-only
              <code className="mx-1">gmail.readonly</code>
              scope, solely to detect and download bank statement PDF
              attachments for import. We do not read, store, or share any other
              email content.
            </li>
            <li>
              <strong>Financial data:</strong> transactions you enter manually,
              upload via bank statement PDF or QR payment slip image, or that
              are imported from connected bank statement emails.
            </li>
            <li>
              <strong>Tax planning data:</strong> income, deduction, and tax
              preference inputs you provide for the tax planner feature.
            </li>
          </ul>
        </Section>

        <Section title="How we use your data">
          <p>
            Your data is used only to provide the app's features to you:
            displaying spending summaries, importing and categorizing
            transactions, and calculating tax estimates. We do not sell your
            data or use it for advertising.
          </p>
          <p>
            Bank statement PDFs and payment slip images may be sent to a
            third-party AI provider (Anthropic) to extract transaction details.
            No data is used to train third-party models.
          </p>
        </Section>

        <Section title="Data storage and security">
          <p>
            OAuth tokens and bank statement passwords are encrypted at rest
            (AES-256-GCM). Data is scoped per user account and is not visible to
            other users.
          </p>
        </Section>

        <Section title="Data retention">
          <p>
            We retain your data for as long as your account is active. If you
            request account deletion, we delete your data within 30 days,
            except where retention is required by law.
          </p>
        </Section>

        <Section title="Your controls">
          <p>
            You can disconnect your Gmail connection at any time from Settings,
            which stops further email access. You can delete individual
            transactions, bank configurations, and API keys from within the app.
            To request full account data deletion, contact us below.
          </p>
        </Section>

        <Section title="Contact">
          <p>
            Questions about this policy or your data can be sent to{" "}
            <a
              href={`mailto:${CONTACT_EMAIL}`}
              className="underline hover:no-underline"
            >
              {CONTACT_EMAIL}
            </a>
            .
          </p>
        </Section>
      </div>
    </div>
  );
}

function Section({
  title,
  children,
}: {
  title: string;
  children: React.ReactNode;
}) {
  return (
    <div className="flex flex-col gap-2">
      <h2 className="text-lg font-semibold">{title}</h2>
      <div className="text-sm text-muted-foreground flex flex-col gap-2">
        {children}
      </div>
    </div>
  );
}
